import { Component, OnInit } from "@angular/core";
import {
  Form,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BaseComponent } from "app/components/base/base.component";
import { Event, EventId } from "app/models/base/event";
import { City } from "app/models/lookups/city";
import { Company } from "app/models/lookups/company";
import { Country } from "app/models/lookups/country";
import { Criterion } from "app/models/query/criterion";
import { OrderByField } from "app/models/query/orderByField";
import { Query } from "app/models/query/query";
import { CityService } from "app/services/lookups/city.service";
import { CompanyService } from "app/services/lookups/company.service";
import { CountryService } from "app/services/lookups/country.service";
import { ClientSettingsService } from "app/services/settings/client-settings.service";
import { isNil } from "lodash";
import { SelectItem } from "primeng";
import { forkJoin, Observable, of } from "rxjs";

@Component({
  selector: "bms-my-company",
  templateUrl: "./my-company.component.html",
  styleUrls: ["./my-company.component.scss"],
  providers: [CompanyService, CityService, CountryService],
})
export class MyCompanyComponent extends BaseComponent implements OnInit {
  activeCompany: Company;
  company: Company;
  companyForm: FormGroup;

  protected cities: City[];
  protected selectCity: SelectItem[] = [{label: '', value: null}];
  protected countries: Country[];
  protected selectCountry: SelectItem[] = [{label: '', value: null}];

  constructor(
    private fb: FormBuilder,
    private clientSettingsService: ClientSettingsService,
    private cityService: CityService,
    private countryService: CountryService,
    private companyService: CompanyService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    forkJoin([
      this.listCities(),
      this.loadCountries(),
      this.loadCompanyData(),
    ]).subscribe(data => {
      if (data[0] && data[0].length > 0) {
        this.cities = data[0];
        this.initSelectCity();
      }
      if (data[1] && data[1].length > 0) {
        this.countries = data[1];
      }
      if (data[2] && data[2].length > 0) {
        this.company = data[2][0];
        this.companyForm.patchValue({
          companyName: this.company.companyName,
          shortName: this.company.shortName,
          email: this.company.email,
          phone: this.company.phone,
          website: this.company.website,
          fax: this.company.fax,
          address: this.company.address,
          cityId: this.company.cityId,
          postalCode: this.company.postalCode,
          stateProvince: this.company.stateProvince,
          countryId: this.company.countryId,
          pib: this.company.pib,
          companyType: this.company.companyType,
          maticniBroj: this.company.maticniBroj,
          vat: this.company.vat,
          industryCode: this.company.industryCode,
          industryCodeDesc: this.company.industryCodeDesc,
          account_number: this.company.account_number,

        });
      }

    });


  }

  initForm() {
    this.companyForm = this.fb.group({
      companyName: ["", [Validators.required, Validators.maxLength(100)]],
      shortName: ["", [Validators.maxLength(50)]],
      email: ["", [Validators.email, Validators.maxLength(100)]],
      phone: ["", [Validators.maxLength(100)]],
      website: ["", [Validators.maxLength(100)]],
      fax: ["", [Validators.maxLength(100)]],
      address: ["", [Validators.maxLength(100)]],
      cityId: [""],
      postalCode: ["", [Validators.maxLength(20)]],
      stateProvince: ["", [Validators.maxLength(50)]],
      countryId: ["", [Validators.maxLength(50)]],
      pib: ["", [Validators.maxLength(20)]],
      companyType: [""],
      maticniBroj: ["", [Validators.maxLength(10)]],
      vat: [""],
      industryCode: [""],
      industryCodeDesc: [""],
      account_number: [""],
    });
  }

  loadCompanyData(): Observable<Company[]> {
    this.activeCompany = this.clientSettingsService.getActiveCompany();
    if (isNil(this.activeCompany) || isNil(this.activeCompany.id)) {
      return of([]);
    }
    const companyId = this.activeCompany.id;
    const qry = new Query();
    qry.and().and(Criterion.equal(Company.TABLENAME, Company.ID, companyId));
    return this.companyService.list(qry);
  }

  listCities(): Observable<City[]> {
    if (this.cities == null) {
      const qry = new Query();
      qry.addOrderBy(new OrderByField(City.TABLENAME, City.NAME, Query.ASC));
      return this.cityService.list(new Query());
    } else {
      return new Observable<City[]>(observer => {
        observer.next(this.cities);
        observer.complete();
      });
    }
  }

  loadCountries(): Observable<Country[]> {
    if (this.countries == null) {
      const qry = new Query();
      qry.addOrderBy(new OrderByField(Country.TABLENAME, Country.NAME, Query.ASC));
      return this.countryService.list(qry);
    } else {
      return new Observable<Country[]>(observer => {
        observer.next(this.countries);
        observer.complete();
      });
    }
  }

  onSubmit(): void {
    if (this.companyForm.valid) {
      const companyData = { ...this.companyForm.value };
      Object.keys(companyData).forEach(key => {
        if (companyData[key] === undefined || companyData[key] === null) {
          delete companyData[key];
        }
      });
      companyData.id = this.activeCompany.id;
      this.companyService.save(companyData).subscribe({
        next: (result) => {
          if (result && result.id) {
            this.setSuccess("Podaci su uspešno sačuvani.");
            this.clientSettingsService.setActiveCompany(result);
            setTimeout(() => {
              this.fireEvent(new Event(EventId.DIALOG_CANCEL, null));
            }, 2000);
          }
        },
        error: (error) => {
          this.setError(error);
        }
      });
    } else {
      this.companyForm.markAllAsTouched();
    }
  }

  protected onShown() {
    super.onShown();
  }

  onClose() {
    this.fireEvent(new Event(EventId.DIALOG_CANCEL, null));
  }

  initSelectCity() {
    this.selectCity = [];
    this.selectCity.push({label: '', value: null});
    if (!isNil(this.cities)) {
      for (const o of this.cities) {
        this.selectCity.push({label: o.name + ' ', value: o.id});
      }
    }
  }

  initSelectCountry() {
    this.selectCountry = [];
    this.selectCountry.push({label: '', value: null});
    if (!isNil(this.countries)) {
      for (const o of this.countries) {
        this.selectCountry.push({label: o.name + ' ', value: o.id});
      }
    }
  }
  helpSelectCountry() {
    this.initSelectCountry();
  }

  helpSelectCity() {
    this.initSelectCity();
  }

  onCityChange(event: any) {
    if (event && event.value) {
      this.companyForm.patchValue({cityId: event.value});
      const selectedCity = this.cities.find(city => city.id === event.value);
      if (selectedCity && selectedCity.postCode) {
        this.companyForm.patchValue({postalCode: selectedCity.postCode});
      }
    } else {
      this.companyForm.patchValue({cityId: null});
    }
  }
}
